import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import Logo from '../Logo';

export default function PartnerSetup({ user, onComplete }) {
    const [loading, setLoading] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [error, setError] = useState(null);
    const [mode, setMode] = useState('initial'); // initial, create, join

    const createCouple = async () => {
        setLoading(true);
        setError(null);
        try {
            const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || 'User';
            const { data: coupleId, error: createError } = await supabase
                .rpc('create_couple', { display_name_input: displayName });

            if (createError) throw createError;

            // Fetch full couple data to complete onboarding
            const { data: fullCouple, error: fetchError } = await supabase
                .from('couples')
                .select(`
          *,
          members:couple_members(*)
        `)
                .eq('id', coupleId)
                .single();

            if (fetchError) throw fetchError;

            onComplete(fullCouple);
        } catch (err) {
            console.error('Error creating couple:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const joinCouple = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || 'User';
            const { data: coupleId, error: joinError } = await supabase.rpc('join_couple', {
                invite_code_input: inviteCode.toUpperCase(),
                display_name_input: displayName
            });

            if (joinError) throw joinError;

            // Fetch full couple data
            const { data: fullCouple, error: fetchError } = await supabase
                .from('couples')
                .select(`
          *,
          members:couple_members(*)
        `)
                .eq('id', coupleId)
                .single();

            if (fetchError) throw fetchError;

            onComplete(fullCouple);
        } catch (err) {
            console.error('Error joining couple:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (mode === 'initial') {
        return (
            <div className="onboarding-shell">
                <div className="onboarding-card">
                    <div className="onboarding-brand">
                        <div className="onboarding-brand-mark">
                            <Logo width={20} height={20} />
                        </div>
                        <span className="onboarding-brand-name">GoHalfsies</span>
                    </div>
                    <h1 className="onboarding-title">Welcome</h1>
                    <p className="onboarding-subtitle">Let's get your shared space set up.</p>

                    <p className="text-footnote" style={{ marginBottom: '20px' }}>
                        To get started, set up a space for you and your partner.
                    </p>

                    <div className="onboarding-actions">
                        <button className="submit-btn" onClick={() => setMode('create')}>
                            Start a new couple
                        </button>
                        <button className="cancel-btn" onClick={() => setMode('join')}>
                            Join my partner
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onboarding-shell">
            <div className="onboarding-card">
                <button
                    className="onboarding-back-btn"
                    onClick={() => setMode('initial')}
                >
                    Back
                </button>

                {mode === 'create' ? (
                    <div className="onboarding-flow">
                        <h2>Create New Space</h2>
                        <p className="text-footnote">We'll generate an invite code for your partner.</p>

                        {error && <div className="auth-error">{error}</div>}

                        <button
                            className="submit-btn"
                            onClick={createCouple}
                            disabled={loading}
                        >
                            {loading ? 'Creating...' : 'Create & Get Code'}
                        </button>
                    </div>
                ) : (
                    <div className="onboarding-flow">
                        <h2>Join Partner</h2>
                        <p className="text-footnote">Enter the invite code from your partner.</p>

                        {error && <div className="auth-error">{error}</div>}

                        <form onSubmit={joinCouple}>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="ENTER CODE"
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value)}
                                    maxLength={6}
                                    required
                                    className="invite-code-input"
                                    disabled={loading}
                                />
                            </div>
                            <button type="submit" className="submit-btn" disabled={loading}>
                                {loading ? 'Joining...' : 'Join'}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
